import { useRef } from 'react';

/**
 * Sonido de "listo" de la pantalla de generación. Extraído tal cual de
 * `LoadingAnimation` al unificarla con la del onboarding
 * (ADR-2026-08-02-pantalla-carga-unificada) — la lógica no cambió.
 *
 * OJO: la inicialización corre en el **cuerpo del render** (no en un
 * `useEffect`) a propósito. El componente se monta dentro del stack del gesto
 * del usuario que tocó "Generar", y ése es el único momento en que iOS deja
 * desbloquear el audio; hacerlo en un efecto (que corre después del paint) ya
 * está fuera del gesto y el sonido no suena nunca.
 *
 * iOS / iPadOS tienen dos quirks que rompían el enfoque "HTML5 Audio
 * muted-unlock" original:
 *   - `audio.volume = 0` es ignorado.
 *   - `audio.muted = true` también es ignorado en muchos iPads/iPhones
 *     para `Audio` (fuera de `<video>`).
 * Resultado: el unlock-play sonaba audible apenas el user tocaba "Generar" en
 * lugar de cuando terminaba la generación.
 *
 * En iOS usamos Web Audio API: el `AudioContext` se desbloquea con
 * `ctx.resume()` durante el user gesture (sin reproducir nada), después bajamos
 * el clicksound vía fetch + decode (async, no hace ruido) y al momento de la
 * "completion" reproducimos un `AudioBufferSourceNode`. Esto respeta el gain
 * (sin reproducir nada al unlock) y suena solo en el momento correcto.
 *
 * En el resto de devices seguimos con el HTML5 Audio approach que ya andaba bien.
 *
 * @param enabled `false` (onboarding) no inicializa nada y deja `play()` como
 *   no-op: evita crear un `AudioContext` en una pantalla que no suena.
 * @returns función para reproducir el sonido.
 */
export function useCompleteSound(enabled: boolean): () => void {
  const completeSoundRef = useRef<HTMLAudioElement | null>(null);
  // Refs para el Web Audio path en iOS.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);

  const isIOSAudioRestricted = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isAppleMobile = /iPad|iPhone|iPod/.test(ua);
    const isIPadDesktopUA =
      navigator.platform === 'MacIntel' &&
      typeof navigator.maxTouchPoints === 'number' &&
      navigator.maxTouchPoints > 1;
    return isAppleMobile || isIPadDesktopUA;
  })();

  if (!enabled) {
    // Sin sonido: nada que inicializar y nada que reproducir.
  } else if (isIOSAudioRestricted) {
    // Inicialización lazy en iOS — primer render solo, dentro del gesture stack
    // que dispara el LoadingAnimation. Resume del ctx + fetch async del clicksound.
    if (!audioCtxRef.current) {
      try {
        const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (Ctor) {
          const ctx: AudioContext = new Ctor();
          audioCtxRef.current = ctx;
          // Resume durante el gesture — esto desbloquea el ctx sin emitir
          // sonido. Si falla, la promesa se rechaza silenciosamente.
          ctx.resume().catch(() => {});
          // Fetch + decode async — cuando el user esté esperando la generación,
          // este pipeline va a estar listo bastante antes.
          fetch('/images/clicksound.m4a')
            .then((res) => res.arrayBuffer())
            .then(
              (buf) =>
                new Promise<AudioBuffer>((resolve, reject) => {
                  // Safari iOS usa la versión callback de decodeAudioData.
                  ctx.decodeAudioData(buf, resolve, reject);
                }),
            )
            .then((audioBuf) => {
              audioBufferRef.current = audioBuf;
            })
            .catch((err) => {
              console.warn('[LoadingAnimation] Web Audio decode falló:', err);
            });
        }
      } catch (err) {
        console.warn('[LoadingAnimation] AudioContext init falló:', err);
      }
    }
  } else if (!completeSoundRef.current) {
    const audio = new Audio('/images/clicksound.m4a');
    audio.preload = 'auto';
    audio.muted = true;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
        console.log('[LoadingAnimation] Sound unlocked successfully');
      })
      .catch((err) => {
        console.warn('[LoadingAnimation] Sound unlock failed:', err.message);
        audio.muted = false;
      });
    completeSoundRef.current = audio;
  }

  /** Reproduce el clicksound — elige el backend según el device. En iOS arma un
   *  BufferSource fresh cada vez (los `AudioBufferSourceNode` no se re-usan). */
  return () => {
    if (!enabled) return;
    try {
      if (isIOSAudioRestricted) {
        const ctx = audioCtxRef.current;
        const buf = audioBufferRef.current;
        if (!ctx || !buf) return;
        // En iOS el ctx puede haber vuelto a 'suspended' si pasó mucho tiempo
        // desde el último resume — intentamos despertarlo de nuevo.
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const source = ctx.createBufferSource();
        source.buffer = buf;
        source.connect(ctx.destination);
        source.start(0);
      } else if (completeSoundRef.current) {
        completeSoundRef.current.currentTime = 0;
        completeSoundRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.warn('[LoadingAnimation] playCompleteSound falló:', err);
    }
  };
}

export default useCompleteSound;
